export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const getFileFromKey = (jobConfig: any, key: string, isExperimental?: boolean) => {
    let config = null;

    for (let i = 0; i < jobConfig.length; i++) {
        if (key && jobConfig[i].key !== key) {
            continue;
        }
        config = jobConfig[i];
    }
    return isExperimental ? config.experimentalFile : config.file;
}

// Preprocess content to convert plain citations [1] into special citation links
export const preprocessContentForCitations = (text: string, stripCitation?: boolean): string => {
    // Match citation patterns like [1], [2], [123], [1, 2], [1,2,3], etc.
    // Use negative lookbehind/lookahead to avoid matching existing markdown links
    if (!text) {
        return text;
    }
    return text.replace(/(?<!\]\()\[(\d+(?:\s*,\s*\d+)*)\](?!\()/g, (match, numbers) => {
        // Split the numbers by comma and clean up whitespace
        const citationNumbers = numbers.split(',').map((num: string) => num.trim());
        
        // Create separate citation links for each number
        const citationLinks = citationNumbers.map((number: string) => {
            const href = `#citation-${number}`;
            if (stripCitation) {
                return "";
            }
            return `[[${number}]](${href} "citation-${number}")`;
        });
        
        // Join them with a space (or you could use other separators like comma+space)
        return citationLinks.join(stripCitation ? '' : ' ');
    });
};

/**
 * Preprocess markdown text to convert timestamp-like patterns into clickable links
 * that will be rendered as <TimestampBubble /> components in React.
 *
 * This function searches for timestamp patterns and replaces each timestamp
 * with a markdown link. The link includes a `#timestamp-...` href and a "timestamp-..."
 * title so that ReactMarkdown can later detect and render them using a custom component.
 *
 * Supported timestamp formats (with and without brackets):
 *     - Date only: 2025-09-24
 *     - Date + time (hours:minutes): 2025-09-24 14:52
 *     - Date + time (hours:minutes:seconds): 2025-09-24 14:52:12
 *     - Any of the above followed by an optional " PT": 2025-09-24 PT
 *     - Multiple timestamps separated by commas: 2025-09-24, 2023-01-19 PT, 2024-02-01 09:30
 */
export const preprocessTimestamps = (text: string): string => {
    if (!text) return text;

    return text.replace(
        /(\()?((?:\d{4}-\d{2}-\d{2}(?: \d{2}:\d{2}(?::\d{2})?)?(?: PT)?)(?:,\s*\d{4}-\d{2}-\d{2}(?: \d{2}:\d{2}(?::\d{2})?)?(?: PT)?)*)(\))?/g,
        (_, openParen, timestamps, closeParen) => {
            const parts = timestamps.split(/\s*,\s*/).filter(Boolean).map((ts: string) => {
                const encoded = encodeURIComponent(ts);
                return `[${ts}](#timestamp-${encoded} "timestamp-${encoded}")`;
            });
            const joined = parts.join(", ");

            // Remove parentheses only if they BOTH exist and fully surround the timestamps
            if (openParen && closeParen) {
                return joined;
            }
            return `${openParen || ""}${joined}${closeParen || ""}`;
        }
    );
};
